/**
 * Shared Head-to-Head (H2H) comparison styling.
 * Used for StatsHeadToHead component styling across the application.
 */

import type React from "react";

export interface H2HStylesOptions {
    leftColor?: string;
    rightColor?: string;
    /** Size variant for bar height */
    size?: "sm" | "md" | "lg";
    /** Text size variant */
    textSize?: "xs" | "sm" | "md";
}

/**
 * Get H2H comparison styles for StatsHeadToHead component.
 * Uses CSS custom properties for dynamic color support.
 *
 * Supports both legacy API (two string arguments) and new options object:
 * @example
 * ```tsx
 * // Legacy API (backward compatible)
 * const styles = getH2hStyles("#00ff85", "#37003c");
 *
 * // New options API
 * const styles = getH2hStyles({ leftColor: "#00ff85", rightColor: "#37003c" });
 *
 * <div style={styles.cssVars}>
 *   <StatsHeadToHead items={items} styles={styles} />
 * </div>
 * ```
 */
export function getH2hStyles(leftColorOrOptions?: string | H2HStylesOptions, rightColor?: string) {
    // Support both legacy (two strings) and new (options object) API
    let options: H2HStylesOptions = {};

    if (typeof leftColorOrOptions === "string") {
        // Legacy API: getH2hStyles(leftColor, rightColor)
        options = { leftColor: leftColorOrOptions, rightColor };
    } else if (leftColorOrOptions) {
        // New API: getH2hStyles({ leftColor, rightColor, ... })
        options = leftColorOrOptions;
    }

    const finalLeftColor = options.leftColor || "var(--primary, #333333)";
    const finalRightColor = options.rightColor || "var(--secondary, #0284c7)";
    const size = options.size || "md";
    const textSize = options.textSize || "sm";

    const textSizeClass = {
        xs: "text-xs",
        sm: "text-sm",
        md: "text-base",
    }[textSize];

    return {
        padding: "md" as const,
        background: "bg-card",
        borderRadius: "lg" as const,
        itemSpacing: "md" as const,
        bar: {
            leftBarColor: "bg-[var(--h2h-left-color)]",
            rightBarColor: "bg-[var(--h2h-right-color)]",
            height: size,
            borderRadius: "full" as const,
        },
        typography: {
            label: `${textSizeClass} font-medium text-foreground`,
            leftValue: `${textSizeClass} font-bold text-[var(--h2h-left-color)]`,
            rightValue: `${textSizeClass} font-bold text-[var(--h2h-right-color)]`,
        },
        cssVars: {
            "--h2h-left-color": finalLeftColor,
            "--h2h-right-color": finalRightColor,
        } as React.CSSProperties,
    };
}

/**
 * Get positive/negative stats styles (green/red bars).
 * Used for HomeVsAwayStats and similar comparison displays.
 */
export function getComparisonStyles(variant: "positive" | "negative") {
    const barColor = variant === "positive" ? "bg-green-500" : "bg-red-500";

    return {
        padding: "md" as const,
        background: "bg-card",
        borderRadius: "lg" as const,
        itemSpacing: "sm" as const,
        bar: {
            leftBarColor: barColor,
            rightBarColor: barColor,
            height: "sm" as const,
            borderRadius: "full" as const,
        },
        typography: {
            label: "text-xs font-medium text-muted-foreground whitespace-nowrap",
            leftValue: "text-xs font-bold text-foreground",
            rightValue: "text-xs font-bold text-foreground",
        },
    };
}
